import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type GateStep = 'auth' | 'trial';

interface AuthGateState {
  isOpen: boolean;
  reason: string | null;
  step: GateStep;
  openGate: (reason: string) => void;
  closeGate: () => void;
  advanceToTrial: () => void;
}

// `AuthGateContext`, `openAuthGate`, and `useAuthGate` are colocated
// with `AuthGateProvider` so the context surface is a single file. The
// imperative `openAuthGate` exists for non-React callers (e.g.
// services/ai.ts) to trigger the gate without prop drilling. See
// badge.tsx for the general react-refresh-on-shared-file rationale.
// eslint-disable-next-line react-refresh/only-export-components
export const AuthGateContext = createContext<AuthGateState | undefined>(undefined);

let _openGate: ((reason: string) => void) | null = null;

// eslint-disable-next-line react-refresh/only-export-components
export function openAuthGate(reason: string) {
  _openGate?.(reason);
}

export function AuthGateProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [step, setStep] = useState<GateStep>('auth');

  const openGate = (r: string) => {
    setReason(r);
    setStep('auth');
    setIsOpen(true);
  };

  const closeGate = () => {
    setIsOpen(false);
    setReason(null);
    setStep('auth');
  };

  const advanceToTrial = () => setStep('trial');

  useEffect(() => {
    _openGate = openGate;
    return () => { _openGate = null; };
  });

  return (
    <AuthGateContext.Provider value={{ isOpen, reason, step, openGate, closeGate, advanceToTrial }}>
      {children}
    </AuthGateContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthGate() {
  const ctx = useContext(AuthGateContext);
  if (!ctx) throw new Error('useAuthGate must be used within AuthGateProvider');
  return ctx;
}
